import { useField } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { DeleteIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { IconButton, Paragraph, Select, tokens } from '@treams/treams-ui';

import { MomentTemplateVariationType } from './types';

interface IOption {
	label: string;
	value: string;
}

interface IRoleOption {
	label: string;
	value: graphql.RoleType;
}

const MomentTemplateVariationElement = styled(Flex)`
	background-color: ${tokens.surface.secondary};
	border-radius: 4px;
`;

const JOB_TITLE_PREFIX = 'jobTitle:';
const ROLE_PREFIX = 'role:';

const MomentTemplateVariation = ({
	momentType,
	templates,
	jobTitles,
	roles,
	name,
	onRemove,
}: {
	momentType: MomentTemplateVariationType;
	templates: IOption[];
	jobTitles: IOption[];
	roles: IRoleOption[];
	name: string;
	onRemove: () => void;
}) => {
	const { t } = useTranslation();
	const [{ value: variation }, , helpers] = useField<graphql.MomentVariation>(name);

	const selectedTemplateID =
		momentType === graphql.MomentType.Feedback
			? variation.feedback?.feedbackTemplate?.id
			: variation.evaluation?.evaluationForm?.id;

	const selectedConditionValue = useMemo(() => {
		if (variation.jobTitle?.id) {
			return `${JOB_TITLE_PREFIX}${variation.jobTitle.id}`;
		}
		if (variation.roleType) {
			return `${ROLE_PREFIX}${variation.roleType}`;
		}
		return undefined;
	}, [variation.jobTitle?.id, variation.roleType]);

	const templateOptions = useMemo(
		() =>
			templates.map((template) => ({
				title: template.label,
				value: template.value,
			})),
		[templates],
	);

	const conditionSections = useMemo(
		() => [
			{
				title: t('common.settings.momentTemplates.alternatives.condition.roles'),
				options: roles.map((role) => ({
					title: role.label,
					value: `${ROLE_PREFIX}${role.value}`,
				})),
			},
			{
				title: t('common.settings.momentTemplates.alternatives.condition.jobTitles'),
				options: jobTitles.map((jobTitle) => ({
					title: jobTitle.label,
					value: `${JOB_TITLE_PREFIX}${jobTitle.value}`,
				})),
			},
		],
		[t, roles, jobTitles],
	);

	const handleTemplateChange = useCallback(
		(value: string | undefined) => {
			if (!value) {
				return;
			}

			if (momentType === graphql.MomentType.Feedback) {
				helpers.setValue({
					...variation,
					feedback: {
						feedbackTemplate: {
							id: value,
						} as unknown as graphql.FeedbackTemplate,
					},
				});
				return;
			}

			helpers.setValue({
				...variation,
				evaluation: {
					evaluationForm: {
						id: value,
					} as unknown as graphql.EvaluationForm,
				},
			});
		},
		[momentType, helpers, variation],
	);

	const handleConditionChange = useCallback(
		(value: string | undefined) => {
			if (!value || typeof value !== 'string') return;

			if (value.startsWith(JOB_TITLE_PREFIX)) {
				const jobTitleID = value.slice(JOB_TITLE_PREFIX.length);

				helpers.setValue({
					...variation,
					jobTitle: {
						id: jobTitleID,
					} as unknown as graphql.JobTitle,
					roleType: undefined,
				});
				return;
			}

			if (value.startsWith(ROLE_PREFIX)) {
				const roleType = value.slice(ROLE_PREFIX.length) as graphql.RoleType;

				helpers.setValue({
					...variation,
					jobTitle: undefined,
					roleType,
				});
			}
		},
		[helpers, variation],
	);

	return (
		<MomentTemplateVariationElement alignItems="center" px={3} py={3} mb={2}>
			<Box width={5 / 12} mr={2}>
				<Paragraph mb={1}>
					{t(
						momentType === graphql.MomentType.Feedback
							? 'common.settings.momentTemplates.variations.feedbackTemplate'
							: 'common.settings.momentTemplates.variations.evaluationTemplate',
					)}
				</Paragraph>

				<Select
					small
					selectedValue={selectedTemplateID}
					options={templateOptions}
					onChange={handleTemplateChange}
				/>
			</Box>

			<Box width={5 / 12} mr={3}>
				<Paragraph mb={1}>{t('common.settings.momentTemplates.alternatives.condition')}</Paragraph>

				<Select
					small
					selectedValue={selectedConditionValue}
					options={conditionSections}
					onChange={handleConditionChange}
				/>
			</Box>

			<Flex width={2 / 12} justifyContent="flex-end">
				<IconButton icon={<DeleteIcon />} onClick={() => onRemove()} type="destructive" />
			</Flex>
		</MomentTemplateVariationElement>
	);
};

export default MomentTemplateVariation;
